/**
 * Invite Friends Modal 组件
 *
 * 功能：
 * - 展示用户的邀请链接（botgo.ai/?ref=CODE）与推荐码，均可一键复制
 * - 展示推荐奖励规则（被推荐人首付赠送积分、推荐人积分奖励、计佣窗口）
 * - 注册 7 天内、首付前、尚无推荐人的用户可补填推荐人的推荐码
 *
 * 归因规则见 docs/product/BotGo_Creator_Affiliate_Manual_V0_2026-11-01.md §6.1
 */

'use client';

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslations } from 'next-intl';
import { useToast } from '@/components/ToastProvider';
import { Check, Copy, Gift, Link2 } from 'lucide-react';

export default function InviteFriendsModal({ isOpen, onClose }) {
  const t = useTranslations('Invite');
  const { showToast } = useToast();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [claimCode, setClaimCode] = useState('');
  const [claiming, setClaiming] = useState(false);

  // 打开弹窗时获取邀请信息
  useEffect(() => {
    if (isOpen) {
      fetchInvite();
    }
  }, [isOpen]);

  const fetchInvite = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch('/api/invite-code');
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to fetch invite details');
      }
      setInvite(data);
    } catch (err) {
      console.error('Failed to fetch invite details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 复制链接或推荐码，2 秒后恢复按钮文案
  const handleCopy = async (field, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy invite value:', err);
      showToast({
        title: t('copyFailed'),
        description: t('copyFailedDesc'),
        type: 'error',
      });
    }
  };

  // 补填推荐码
  const handleClaim = async (event) => {
    event.preventDefault();
    if (!claimCode.trim() || claiming) return;
    try {
      setClaiming(true);
      const res = await fetch('/api/referral/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: claimCode }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || t('claimFailed'));
      }
      setClaimCode('');
      setInvite((prev) => ({ ...prev, referred: true, canClaimReferral: false }));
      showToast({
        title: t('claimSuccess', { credits: invite?.welcomeCredits ?? 500 }),
        type: 'success',
      });
    } catch (err) {
      showToast({
        title: t('claimFailed'),
        description: err.message,
        type: 'error',
      });
    } finally {
      setClaiming(false);
    }
  };

  const credits = (invite?.welcomeCredits ?? 500).toLocaleString();

  const renderCopyRow = (field, label, value, buttonLabel, Icon) => (
    <div className="space-y-2">
      <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">{label}</div>
      <div className="flex items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-neutral-700/60 bg-neutral-800/80 px-3 py-2.5">
          <Icon className="h-4 w-4 shrink-0 text-amber-400" />
          <span className="truncate text-sm text-white">{value}</span>
        </div>
        <Button
          type="button"
          onClick={() => handleCopy(field, value)}
          className="shrink-0 bg-white text-black transition-transform hover:scale-[1.02] hover:bg-neutral-200"
        >
          {copiedField === field ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
          {copiedField === field ? t('copied') : buttonLabel}
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-neutral-700/50 bg-neutral-900 p-6 sm:max-w-lg sm:p-8">
        <DialogTitle className="text-center text-xl font-semibold text-white">
          {t('title')}
        </DialogTitle>

        {loading ? (
          <div className="py-8 text-center text-neutral-400">{t('loading')}</div>
        ) : error ? (
          <div className="py-8 text-center text-red-400">{t('loadFailed')}</div>
        ) : (
          <div className="flex flex-col gap-6">
            <DialogDescription className="text-center text-sm text-neutral-400">
              {t('subtitle', { credits })}
            </DialogDescription>

            {renderCopyRow('link', t('linkLabel'), invite?.link, t('copyLink'), Link2)}
            {renderCopyRow('code', t('codeLabel'), invite?.code, t('copyCode'), Gift)}

            {/* 奖励规则 */}
            <div className="space-y-2 rounded-xl border border-amber-500/20 bg-gradient-to-br from-amber-500/10 to-orange-500/5 p-4">
              <div className="text-sm font-semibold text-amber-300">{t('rewardsTitle')}</div>
              <ul className="list-disc space-y-1.5 pl-4 text-sm text-neutral-300">
                <li>{t('rewardFriend', { credits })}</li>
                <li>{t('rewardYou')}</li>
                <li className="text-neutral-400">{t('rewardRules')}</li>
              </ul>
            </div>

            {/* 补填推荐码 */}
            {invite?.canClaimReferral ? (
              <form onSubmit={handleClaim} className="space-y-2 border-t border-neutral-800 pt-5">
                <div className="text-sm font-medium text-white">{t('claimTitle')}</div>
                <p className="text-xs text-neutral-400">{t('claimDescription')}</p>
                <div className="flex gap-2">
                  <Input
                    value={claimCode}
                    onChange={(event) => setClaimCode(event.target.value.toUpperCase())}
                    placeholder={t('claimPlaceholder')}
                    maxLength={20}
                    className="border-neutral-700 bg-neutral-800 text-white placeholder:text-neutral-500 focus-visible:ring-amber-500"
                  />
                  <Button
                    type="submit"
                    disabled={!claimCode.trim() || claiming}
                    className="shrink-0 bg-gradient-to-r from-amber-500 to-orange-500 text-black hover:from-amber-400 hover:to-orange-400"
                  >
                    {t('claimSubmit')}
                  </Button>
                </div>
              </form>
            ) : invite?.referred ? (
              <p className="border-t border-neutral-800 pt-5 text-center text-xs text-neutral-500">{t('referredNote')}</p>
            ) : null}

            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="w-full border-neutral-700 bg-transparent text-neutral-300 hover:bg-neutral-800 hover:text-white"
            >
              {t('close')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
